import React, { useState, useEffect } from 'react';
import { HeartIcon } from '@heroicons/react/24/outline';
import { getBirds, Bird } from '../../api/birds';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../ui/BrandModal';

interface HealthCheckupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (checkupData: any) => Promise<void>;
  birds?: Bird[];
}

const HealthCheckupModal: React.FC<HealthCheckupModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  birds = [],
}) => {
  const [formData, setFormData] = useState({
    birdId: '',
    checkupDate: '',
    checkupType: 'ROUTINE',
    notes: '',
    priority: 'MEDIUM',
  });
  const [loading, setLoading] = useState(false);
  const [availableBirds, setAvailableBirds] = useState<Bird[]>([]);

  useEffect(() => {
    if (isOpen) {
      if (Array.isArray(birds) && birds.length > 0) {
        setAvailableBirds(birds);
      } else {
        loadBirds();
      }
    }
  }, [isOpen, birds]);

  const loadBirds = async () => {
    try {
      const response = await getBirds();
      setAvailableBirds(response);
    } catch (error) {
      console.error('Error loading birds:', error);
      setAvailableBirds([]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.birdId || !formData.checkupDate) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      await onSubmit({
        ...formData,
        checkupDate: new Date(formData.checkupDate).toISOString(),
      });
      handleClose();
    } catch (error) {
      console.error('Error scheduling health checkup:', error);
      alert('Failed to schedule health checkup');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      birdId: '',
      checkupDate: '',
      checkupType: 'ROUTINE',
      notes: '',
      priority: 'MEDIUM',
    });
    onClose();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const selectedBird = Array.isArray(availableBirds) ? availableBirds.find(bird => bird.id === formData.birdId) : undefined;

  return (
    <BrandModal open={isOpen} onClose={handleClose} maxWidthClass="max-w-2xl">
      <BrandModalHeader
        title={
          <span className="inline-flex items-center">
            <HeartIcon className="h-6 w-6 text-red-500 mr-3" />
            Schedule Health Checkup
          </span>
        }
        onClose={handleClose}
      />
      <form onSubmit={handleSubmit}>
        <BrandModalBody className="space-y-6">
                  {/* Bird Selection */}
                  <div>
                    <label htmlFor="birdId" className="block text-sm font-medium text-slate-700 mb-2">
                      Select Bird *
                    </label>                    <select
                      id="birdId"
                      name="birdId"
                      value={formData.birdId}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-cyan-500 focus:border-cyan-500"
                      required
                    >
                      <option key="empty-option-1753461297322" value="">Choose a bird...</option>
                      <option key="ALL_BIRDS-1753461297322" value="ALL_BIRDS" className="font-medium text-cyan-600">
                        🔵 All Birds / Flock
                      </option>
                      {Array.isArray(availableBirds) && availableBirds.map((bird) => (
                        <option key={bird.id} value={bird.id}>
                          {bird.bandId} - {bird.species?.commonName || 'Unknown Species'}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Selected Bird Info */}
                  {selectedBird && (
                    <div className="bg-slate-50/80 p-4 rounded-lg">
                      <h4 className="text-sm font-medium text-slate-900 mb-2">Selected Bird Information</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-slate-500">Band ID:</span>
                          <span className="ml-2 font-medium">{selectedBird.bandId}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Species:</span>
                          <span className="ml-2 font-medium">{selectedBird.species?.commonName || 'Unknown'}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Current Status:</span>
                          <span className="ml-2 font-medium">{selectedBird.status}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Health Status:</span>
                          <span className="ml-2 font-medium">{selectedBird.healthStatus || 'Unknown'}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Checkup Date */}
                  <div>
                    <label htmlFor="checkupDate" className="block text-sm font-medium text-slate-700 mb-2">
                      Checkup Date *
                    </label>
                    <input
                      type="datetime-local"
                      id="checkupDate"
                      name="checkupDate"
                      value={formData.checkupDate}
                      onChange={handleInputChange}
                      min={new Date().toISOString().slice(0, 16)}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-cyan-500 focus:border-cyan-500"
                      required
                    />
                  </div>

                  {/* Checkup Type */}
                  <div>
                    <label htmlFor="checkupType" className="block text-sm font-medium text-slate-700 mb-2">
                      Checkup Type
                    </label>
                    <select
                      id="checkupType"
                      name="checkupType"
                      value={formData.checkupType}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-cyan-500 focus:border-cyan-500"
                    >
                      <option key="ROUTINE-1753461297322" value="ROUTINE">Routine Checkup</option>
                      <option key="WELLNESS-1753461297322" value="WELLNESS">Wellness Exam</option>
                      <option key="FOLLOW_UP-1753461297322" value="FOLLOW_UP">Follow-up Checkup</option>
                      <option key="EMERGENCY-1753461297322" value="EMERGENCY">Emergency Checkup</option>
                      <option key="PRE_BREEDING-1753461297322" value="PRE_BREEDING">Pre-breeding Checkup</option>
                      <option key="POST_BREEDING-1753461297322" value="POST_BREEDING">Post-breeding Checkup</option>
                    </select>
                  </div>

                  {/* Priority */}
                  <div>
                    <label htmlFor="priority" className="block text-sm font-medium text-slate-700 mb-2">
                      Priority Level
                    </label>
                    <select
                      id="priority"
                      name="priority"
                      value={formData.priority}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-cyan-500 focus:border-cyan-500"
                    >
                      <option key="LOW-1753461297322" value="LOW">Low</option>
                      <option key="MEDIUM-1753461297322" value="MEDIUM">Medium</option>
                      <option key="HIGH-1753461297322" value="HIGH">High</option>
                      <option key="URGENT-1753461297322" value="URGENT">Urgent</option>
                    </select>
                  </div>

                  {/* Notes */}
                  <div>
                    <label htmlFor="notes" className="block text-sm font-medium text-slate-700 mb-2">
                      Notes
                    </label>
                    <textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows={3}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-cyan-500 focus:border-cyan-500"
                      placeholder="Any additional notes or special instructions..."
                    />
                  </div>

        </BrandModalBody>
        <BrandModalFooter>
          <button
            type="button"
            className="btn btn-secondary text-sm"
            onClick={handleClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary text-sm disabled:opacity-50"
          >
            {loading ? 'Scheduling...' : 'Schedule Checkup'}
          </button>
        </BrandModalFooter>
      </form>
    </BrandModal>
  );
};

export default HealthCheckupModal; 